import { useEffect, useState } from 'react';
import { Loader2, Save, RefreshCw, AlertTriangle, FileSignature } from 'lucide-react';
import api from '../../api';
import { useToast } from '../../hooks/use-toast';

export default function ContractTemplateEditor() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tpl, setTpl] = useState({ title: '', content: '', version: 1 });

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/contract-template');
      setTpl(res.data);
    } catch (e) { toast({ title: 'Erreur', description: 'Impossible de charger le modèle.', duration: 10000 }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put('/admin/contract-template', { title: tpl.title, content: tpl.content });
      setTpl(res.data);
      toast({ title: 'Nouvelle version enregistrée', description: `Modèle contrat v${res.data.version}. Les contrats déjà acceptés ne sont pas affectés.`, duration: 10000 });
    } catch (e) { toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Sauvegarde impossible.', duration: 10000 }); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-8 flex items-center gap-2 text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Chargement du modèle…</div>;

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-6" data-testid="contract-template-editor">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <FileSignature className="w-5 h-5 text-brand-navy" />
          <h2 className="font-bold text-brand-navy">Modèle de contrat commercial (Étape 04)</h2>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-brand-navy text-white font-bold uppercase tracking-wider">v{tpl.version}</span>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-300 text-brand-navy text-xs font-semibold hover:border-brand-navy">
            <RefreshCw className="w-3.5 h-3.5" /> Recharger
          </button>
          <button data-testid="save-contract-template" onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-brand-yellow text-brand-navy text-xs font-bold hover:bg-yellow-400 disabled:opacity-50">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Enregistrer une nouvelle version
          </button>
        </div>
      </div>

      <div className="p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800 mb-4">
        <b>Note :</b> ce contenu est un brouillon — les clauses doivent être validées par vos conseillers juridiques avant utilisation. Les contrats déjà acceptés conservent leur version d'origine.
      </div>

      <label className="block text-xs font-semibold text-brand-navy uppercase tracking-wider mb-1">Titre du document</label>
      <input value={tpl.title} onChange={(e) => setTpl({ ...tpl, title: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:border-brand-navy mb-4" />

      <label className="block text-xs font-semibold text-brand-navy uppercase tracking-wider mb-1">Contenu (HTML)</label>
      <textarea value={tpl.content} onChange={(e) => setTpl({ ...tpl, content: e.target.value })} rows={18} className="w-full px-3 py-2 border border-gray-200 rounded font-mono text-xs leading-relaxed focus:outline-none focus:border-brand-navy" />
    </div>
  );
}
